import { useEffect, useMemo, useState } from "react";
import { onValue, push, ref, serverTimestamp, set, update } from "firebase/database";
import {
  ArrowLeft,
  Building2,
  CalendarClock,
  Check,
  Copy,
  Edit3,
  ExternalLink,
  Globe2,
  Mail,
  MapPin,
  MessageSquarePlus,
  Phone,
  Send,
  Star,
  StickyNote,
  Trash2
} from "lucide-react";
import { database } from "../firebase";
import { buildDesignPreviewUrls, buildMailtoLink, buildPreviewUrl, displayNameForUser, formatTimestamp, googleRatingLabel, googleRatingTone, websiteQuality } from "../utils";
import { industryForContact, industryLabel } from "../industries";

const statusOptions = [
  ["new", "Neu"],
  ["contacted", "Kontaktiert"],
  ["not_reached", "Nicht erreicht"],
  ["follow_up", "Follow-up"],
  ["review", "Nachprüfen"],
  ["postponed", "Zurückgestellt"],
  ["interested", "Interessiert"],
  ["closed", "Abgeschlossen"],
  ["not_interested", "Kein Interesse"]
];
const websiteQualityLabels = { none: "Keine Website", poor: "Schlecht", average: "Durchschnittlich", good: "Gut" };
const enrichmentLabels = { pending: "Ausstehend", partial: "Teilweise", complete: "Vollständig" };

export default function ContactDetail({ contact, user, templates, onBack, onEdit, onDelete }) {
  const [notes, setNotes] = useState([]);
  const [note, setNote] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(templates[0]?.id || "");
  const [copied, setCopied] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [selectedStatus, setSelectedStatus] = useState(contact.status);
  const [statusSaving, setStatusSaving] = useState(false);
  const [statusError, setStatusError] = useState("");
  const previewUrl = buildPreviewUrl(contact.id);
  const designPreviewUrls = buildDesignPreviewUrls(contact);
  const previewIndustryLabel = industryLabel(industryForContact(contact));

  useEffect(() => {
    setSelectedStatus(contact.status);
    setStatusError("");
  }, [contact.id, contact.status]);

  useEffect(() => {
    const notesRef = ref(database, `admin/contactNotes/${contact.id}`);
    return onValue(notesRef, (snapshot) => {
      const value = snapshot.val() || {};
      setNotes(Object.entries(value).map(([id, item]) => ({ id, ...item })).sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0)));
    });
  }, [contact.id]);

  useEffect(() => {
    if (!templates.some((template) => template.id === selectedTemplate)) setSelectedTemplate(templates[0]?.id || "");
  }, [templates, selectedTemplate]);

  const template = useMemo(() => templates.find((item) => item.id === selectedTemplate) || templates[0], [selectedTemplate, templates]);

  async function addNote(event) {
    event.preventDefault();
    const text = note.trim();
    if (!text) return;
    setSavingNote(true);
    try {
      const noteRef = push(ref(database, `admin/contactNotes/${contact.id}`));
      await set(noteRef, {
        text,
        createdAt: serverTimestamp(),
        createdBy: user.uid,
        createdByName: displayNameForUser(user)
      });
      await update(ref(database, `admin/contacts/${contact.id}`), {
        lastNoteAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        updatedBy: user.uid
      });
      setNote("");
    } finally {
      setSavingNote(false);
    }
  }

  async function changeStatus(event) {
    const nextStatus = event.target.value;
    setSelectedStatus(nextStatus);
    setStatusSaving(true);
    setStatusError("");
    try {
      await update(ref(database, `admin/contacts/${contact.id}`), {
        status: nextStatus,
        updatedAt: serverTimestamp(),
        updatedBy: user.uid
      });
    } catch {
      setSelectedStatus(contact.status);
      setStatusError("Der Status konnte nicht gespeichert werden. Bitte erneut versuchen.");
    } finally {
      setStatusSaving(false);
    }
  }

  async function copyPreview() {
    await navigator.clipboard.writeText(previewUrl);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1600);
  }

  async function deleteContact() {
    if (deleteConfirmation !== "DELETE") return;
    setDeleting(true);
    setDeleteError("");
    try {
      await onDelete(contact.id);
    } catch (error) {
      setDeleteError(error.message || "Der Kontakt konnte nicht gelöscht werden.");
      setDeleting(false);
    }
  }

  const address = contact.address?.formatted || [contact.address?.line1, contact.address?.city, contact.address?.state].filter(Boolean).join(", ");
  const poorWebsite = websiteQuality(contact.website) === "poor";

  return (
    <section className="detail-panel">
      <header className="detail-header">
        <button className="icon-button mobile-back" onClick={onBack} aria-label="Zurück zur Kontaktliste"><ArrowLeft /></button>
        <div className="company-avatar">{contact.companyName?.slice(0, 2).toUpperCase()}</div>
        <div className="detail-title"><p className="eyebrow">Prospect</p><h2>{contact.companyName}</h2><p className="contact-person">{contact.contactName || "Kein Ansprechpartner hinterlegt"}</p><div className="detail-badges"><div className="industry-summary"><Building2 /><strong>{industryLabel(industryForContact(contact))}</strong></div>{poorWebsite && <div className="website-quality-summary" title={contact.website ? "Die hinterlegte Website verwendet kein HTTPS." : "Für diesen Kontakt ist keine Website hinterlegt."}><Globe2 /><strong>Keine/Schlechte Website</strong></div>}{Number.isFinite(contact.googleRating) && <div className={`google-rating-summary rating-${googleRatingTone(contact.googleRating)}`}><Star /><strong>{contact.googleRating.toFixed(1)}</strong><span>{googleRatingLabel(contact.googleRating)} · {(contact.googleRatingCount || 0).toLocaleString("de-DE")} Google-Bewertungen</span></div>}</div></div>
        <div className="detail-header-actions"><button className="icon-button soft" onClick={onEdit} aria-label="Kontakt bearbeiten"><Edit3 /></button><button className="icon-button danger-button" onClick={() => setDeleteOpen(true)} aria-label="Kontakt löschen"><Trash2 /></button></div>
      </header>

      <div className="status-line">
        <label>Status<select className={`status-select status-${selectedStatus}`} value={selectedStatus} onChange={changeStatus} disabled={statusSaving}>{statusOptions.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select>{statusSaving && <small className="status-saving">Wird gespeichert …</small>}</label>
        <span><CalendarClock /> Aktualisiert {formatTimestamp(contact.updatedAt)}</span>
        {statusError && <p className="status-error" role="alert">{statusError}</p>}
      </div>

      <div className="quick-actions">
        {contact.phone?.e164 ? <a className="quick-action call" href={`tel:${contact.phone.e164}`}><Phone /><span><strong>Anrufen</strong><small>{contact.phone.display}</small></span></a> : <div className="quick-action disabled"><Phone /><span><strong>Keine Nummer</strong><small>Kontakt bearbeiten</small></span></div>}
        {contact.email ? <a className="quick-action" href={template ? buildMailtoLink(contact, template, displayNameForUser(user), user.email || "") : `mailto:${contact.email}?from=${encodeURIComponent(user.email || "")}`}><Mail /><span><strong>E-Mail öffnen</strong><small>{contact.email}</small></span></a> : <div className="quick-action disabled"><Mail /><span><strong>Keine E-Mail</strong><small>Kontakt bearbeiten</small></span></div>}
        {contact.googleMapsUrl ? <a className="quick-action" href={contact.googleMapsUrl} target="_blank" rel="noreferrer"><MapPin /><span><strong>Google Maps</strong><small>Profil öffnen</small></span><ExternalLink className="trailing-icon" /></a> : <div className="quick-action disabled"><MapPin /><span><strong>Kein Maps-Link</strong><small>Kontakt bearbeiten</small></span></div>}
      </div>

      <div className="detail-grid">
        <section className="info-card">
          <div className="card-heading"><Building2 /><div><p className="eyebrow">Kontaktdaten</p><h3>Unternehmen</h3></div></div>
          <dl>
            <div><dt>Ansprechpartner</dt><dd>{contact.contactName || "—"}{contact.contactRole ? ` · ${contact.contactRole}` : ""}</dd></div>
            {contact.contactSource && <div><dt>Personenquelle</dt><dd><a className="inline-link" href={contact.contactSource} target="_blank" rel="noreferrer"><ExternalLink /> Quelle öffnen</a></dd></div>}
            <div><dt>E-Mail</dt><dd>{contact.email || "—"}</dd></div>
            {contact.emailSource && <div><dt>E-Mail-Quelle</dt><dd><a className="inline-link" href={contact.emailSource} target="_blank" rel="noreferrer"><ExternalLink /> Quelle öffnen</a></dd></div>}
            <div><dt>Website</dt><dd>{contact.website ? <a className="inline-link" href={contact.website} target="_blank" rel="noreferrer"><Globe2 /> {contact.website}</a> : "—"}</dd></div>
            <div><dt>Website-Qualität</dt><dd>{websiteQualityLabels[contact.websiteQuality] || (poorWebsite ? "Schlecht" : "Nicht bewertet")}</dd></div>
            {contact.websiteNotes && <div><dt>Website-Hinweis</dt><dd>{contact.websiteNotes}</dd></div>}
            <div><dt>Telefon</dt><dd>{contact.phone?.display || "—"}</dd></div>
            <div><dt>Anschrift</dt><dd>{address || "—"}</dd></div>
            {contact.googlePlaceId && <div><dt>Google Place ID</dt><dd className="mono">{contact.googlePlaceId}</dd></div>}
            <div><dt>Lead-Quelle</dt><dd>{contact.leadSource || "—"}</dd></div>
            <div><dt>Kampagne</dt><dd>{contact.campaign || "—"}</dd></div>
            <div><dt>Branche / Land</dt><dd>{[industryLabel(industryForContact(contact)), contact.country].filter(Boolean).join(" · ")}</dd></div>
            <div><dt>Anreicherung</dt><dd>{enrichmentLabels[contact.enrichmentStatus] || "—"}</dd></div>
          </dl>
        </section>

        <section className="info-card preview-card">
          <div className="card-heading"><ExternalLink /><div><p className="eyebrow">Live-Demo</p><h3>Persönliche Vorschau</h3></div></div>
          <p className="preview-industry">Branche: <strong>{previewIndustryLabel}</strong></p>
          {contact.previewEnabled === false ? <p className="muted">Die Vorschau ist für diesen Kontakt deaktiviert.</p> : designPreviewUrls.length === 0 ? <p>Für die Branche {previewIndustryLabel} ist noch keine passende Vorschau verfügbar.</p> : <>
            <p>Alle drei Designs zeigen die passende Branche mit Firmenname, Ort und Kontaktdaten dieses Kontakts. Der bisherige Hauptlink bleibt unverändert.</p>
            <div className="link-box"><span>{previewUrl}</span><button onClick={copyPreview} aria-label="Hauptlink kopieren">{copied ? <Check /> : <Copy />}</button></div>
            <a className="button secondary wide" href={previewUrl} target="_blank" rel="noreferrer">Vorschau öffnen <ExternalLink /></a>
            <div className="preview-designs" aria-label={`Drei Vorschau-Designs für ${previewIndustryLabel}`}>
              {designPreviewUrls.map(({ design, label, url }) => <a className="preview-design-link" key={design} href={url} target="_blank" rel="noreferrer"><span><strong>{label}</strong><small>{url}</small></span><ExternalLink /></a>)}
            </div>
          </>}
        </section>
      </div>

      {contact.email && template && (
        <section className="email-strip">
          <div><p className="eyebrow light">Outreach</p><strong>Persönliche E-Mail vorbereiten</strong></div>
          <div className="email-template-choice"><select value={selectedTemplate} onChange={(event) => setSelectedTemplate(event.target.value)}>{templates.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select>{template.cc && <small>CC: {template.cc}</small>}{template.bcc && <small>BCC: {template.bcc}</small>}</div>
          <a className="button white" href={buildMailtoLink(contact, template, displayNameForUser(user), user.email || "")}><Send /> Im E-Mail-Programm öffnen</a>
        </section>
      )}

      <section className="notes-card">
        <div className="card-heading"><StickyNote /><div><p className="eyebrow">Verlauf</p><h3>Gesprächsnotizen</h3></div><span className="count-badge">{notes.length}</span></div>
        <form className="note-form" onSubmit={addNote}>
          <textarea value={note} onChange={(event) => setNote(event.target.value)} maxLength="1000" placeholder="z. B. Nicht erreicht – am Montag erneut versuchen …" aria-label="Neue Gesprächsnotiz" />
          <button className="button primary" disabled={savingNote || !note.trim()}><MessageSquarePlus /> Notiz speichern</button>
        </form>
        <div className="timeline">
          {notes.length === 0 ? <div className="empty-state small"><StickyNote /><strong>Noch keine Notizen</strong><p>Der Gesprächsverlauf erscheint chronologisch an dieser Stelle.</p></div> : notes.map((item) => (
            <article className="timeline-item" key={item.id}>
              <div className="timeline-dot" />
              <div className="timeline-meta"><strong>{formatTimestamp(item.createdAt)} · {item.createdByName}</strong></div>
              <p>{item.text}</p>
            </article>
          ))}
        </div>
      </section>

      {deleteOpen && <div className="confirm-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && !deleting && setDeleteOpen(false)}>
        <section className="delete-dialog" role="alertdialog" aria-modal="true" aria-labelledby="delete-contact-title" aria-describedby="delete-contact-description">
          <div className="delete-dialog-icon"><Trash2 /></div>
          <h2 id="delete-contact-title">Kontakt unwiderruflich löschen?</h2>
          <p id="delete-contact-description">Dabei werden <strong>{contact.companyName}</strong>, sämtliche Gesprächsnotizen und die persönliche Website-Vorschau vollständig gelöscht. Diese Aktion kann nicht rückgängig gemacht werden.</p>
          <label className="field"><span>Zum Bestätigen <code>DELETE</code> eingeben</span><input autoFocus value={deleteConfirmation} onChange={(event) => setDeleteConfirmation(event.target.value)} autoComplete="off" spellCheck="false" /></label>
          {deleteError && <div className="error-box" role="alert">{deleteError}</div>}
          <div className="delete-dialog-actions"><button type="button" className="button ghost" onClick={() => setDeleteOpen(false)} disabled={deleting}>Abbrechen</button><button type="button" className="button danger" onClick={deleteContact} disabled={deleteConfirmation !== "DELETE" || deleting}><Trash2 />{deleting ? "Wird gelöscht …" : "Alles endgültig löschen"}</button></div>
        </section>
      </div>}
    </section>
  );
}
